/**
 * Step 2 — Template input.
 *
 * Lists platform + org templates from `creativeBriefTemplatesApi.list()`,
 * groups them with platform first, and emits `onSelect(template)` so the
 * parent overlay can pre-fill the draft preview.
 */

import { useEffect, useMemo, useState } from 'react';
import { Check, LayoutTemplate, Loader2 } from 'lucide-react';
import {
  creativeBriefTemplatesApi,
  type BriefTemplate,
} from '@/services/api';

interface Props {
  organizationId?: string;
  selectedTemplateId?: string;
  onSelect: (template: BriefTemplate) => void;
}

export function TemplateInput({
  organizationId,
  selectedTemplateId,
  onSelect,
}: Props) {
  const [templates, setTemplates] = useState<BriefTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    creativeBriefTemplatesApi
      .list(organizationId)
      .then((res) => {
        if (!cancelled) setTemplates(res.templates ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message || 'Load failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const grouped = useMemo(() => {
    const platform = templates.filter((t) => t.is_platform);
    const org = templates.filter((t) => !t.is_platform);
    return { platform, org };
  }, [templates]);

  return (
    <div className="space-y-4">
      <div className="text-[11px] text-slate-500">
        Pick a template to pre-fill core sections.  You can edit any section
        after the brief is created.
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500 py-8 justify-center">
          <Loader2 size={14} className="animate-spin" />
          Loading templates…
        </div>
      )}

      {error && (
        <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {grouped.platform.length > 0 && (
            <Group label="Platform templates">
              {grouped.platform.map((t) => (
                <TemplateRow
                  key={t.template_id}
                  template={t}
                  selected={t.template_id === selectedTemplateId}
                  onClick={() => onSelect(t)}
                />
              ))}
            </Group>
          )}
          {grouped.org.length > 0 && (
            <Group label="Organization templates">
              {grouped.org.map((t) => (
                <TemplateRow
                  key={t.template_id}
                  template={t}
                  selected={t.template_id === selectedTemplateId}
                  onClick={() => onSelect(t)}
                />
              ))}
            </Group>
          )}
        </>
      )}
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
        {label}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TemplateRow({
  template,
  selected,
  onClick,
}: {
  template: BriefTemplate;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-xl border px-4 py-3 transition flex items-center gap-3 ${
        selected
          ? 'border-slate-700 bg-slate-50 shadow-sm'
          : 'border-slate-200 bg-white hover:border-slate-400 hover:shadow-sm'
      }`}
    >
      <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
        <LayoutTemplate size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold text-slate-900 truncate">
            {template.name}
          </h4>
          <span className="text-[9px] font-mono uppercase tracking-wider text-slate-400">
            {template.template_type}
          </span>
        </div>
        <p className="text-[12px] text-slate-600 mt-0.5 leading-snug line-clamp-2">
          {template.description}
        </p>
      </div>
      {selected && <Check size={14} className="text-emerald-500 shrink-0" />}
    </button>
  );
}
